import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    merchantSelectors,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    sharedConfigurationSelectors,
    StandardButtonWithSpinner,
    TextButton,
    useSelector,
} from "swiipe.portal.shared"
import LegalTextLinkPopup from "../../../components/legal/LegalTextLinkPopup"
import CancelService from "../../../components/merchant/CancelService"
import GoBackContainer from "../../../components/navigations/GoBackContainer"
import { SharedCancelContainer } from "../../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../../services/imageLanguageService"
import { removeSchemeFromHostName } from "../../../services/merchantService"
import { useRequiredUserRelations } from "../../../services/userRelationService"
import { StoreState } from "../../../store/StoreState"
import { gatewaySelectors, IGatewayDto } from "../../../store/reducers/gatewayReducer"
import {
    digitealOnboardingConnectThunk,
    getDigitealOnboardingDataThunk,
    IDigitealGetOnboardingDataResponse,
} from "../../../store/thunks/digitealThunks"
import { getGatewaysForMerchantThunk } from "../../../store/thunks/gatewayThunks"
import { disableOnboardingThunk } from "../../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../../store/useReduxDispatch"
import "./BancontactOnboardingPage.scss"

interface IBancontactPageProps {
    webshopIds?: string[]
    isEdit?: boolean
    onGoBack: () => void
    onOnboarded: () => void
}

interface IBancontactPageInnerProps extends IBancontactPageProps {
    merchantDetails: MixedMerchantDetails
    selectedWebshopId: string
}

const BancontactPage = (props: IBancontactPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }])
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId =
        props.webshopIds && props.webshopIds?.length > 0 ? props.webshopIds[0] : useSelector(merchantSelectors.selectedWebshopId)

    if (!merchantDetails || !merchantDetails.webshops.length || !selectedWebshopId) {
        return null
    }

    return <BancontactPageInner merchantDetails={merchantDetails} selectedWebshopId={selectedWebshopId} {...props} />
}

const BancontactPageInner = ({
    merchantDetails,
    selectedWebshopId,
    isEdit,
    onGoBack,
    onOnboarded,
}: IBancontactPageInnerProps) => {
    const dispatch = useReduxDispatch()
    const { t } = useTranslation()
    const env = useSelector(sharedConfigurationSelectors.environment)

    const [onboardingData, setOnboardingData] = useState<IDigitealGetOnboardingDataResponse | undefined>()
    const [showCancelService, setShowCancelService] = useState(false)

    const webshopName = removeSchemeFromHostName(
        merchantDetails.webshops.find((w) => w.webshopId == selectedWebshopId)?.hostName || ""
    )

    useEffect(() => {
        if (isEdit && selectedWebshopId) {
            ;(async () => {
                const onboardingData = await dispatch(getDigitealOnboardingDataThunk(selectedWebshopId))

                setOnboardingData(onboardingData)
            })()
        }
    }, [isEdit, selectedWebshopId])

    const merchantId = merchantDetails.swMerchant.swMerchantId
    useEffect(() => {
        dispatch(getGatewaysForMerchantThunk(merchantId, false))
    }, [merchantId])

    const gateway = useSelector<StoreState, IGatewayDto | undefined>((state) =>
        gatewaySelectors.getGateway(state, merchantId, "Bancontact", selectedWebshopId)
    )

    return (
        <>
            {showCancelService && gateway && (
                <CancelService
                    serviceName={t("bancontactOnboarding.bancontact")}
                    cancelText={t("cancelService.bancontact.cancelText", { webshopName })}
                    customCancelledText={t("cancelService.bancontact.cancelledText")}
                    onGoBack={() => setShowCancelService(false)}
                    onCancel={async () => {
                        await dispatch(disableOnboardingThunk(merchantId, selectedWebshopId, gateway.gatewayId, "Bancontact"))

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantId,
                                "GeneralServiceCancellationEopEmail",
                                t("serviceNames.bancontact"),
                                true
                            )
                        )
                    }}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit} disableDefaultPaddingBottom>
                    <div className="bancontact-onboarding-container">
                        <div className="bancontact-form-head">
                            <img className="bancontact-logo" src={getImage("bancontactLogo")} />
                        </div>

                        {!isEdit && (
                            <>
                                <StandardButtonWithSpinner
                                    title={t("commononboardingtexts.submit")}
                                    onClick={async () => {
                                        try {
                                            await dispatch(
                                                digitealOnboardingConnectThunk({
                                                    swMerchantId: merchantDetails.swMerchant.swMerchantId,
                                                    webshopIds: [selectedWebshopId],
                                                    paymentType: "Bancontact",
                                                })
                                            )

                                            onOnboarded()
                                        } catch {
                                            //Catch to stop showing spinner
                                        }
                                    }}
                                />
                                <TextButton
                                    text={t("commononboardingtexts.back")}
                                    className={cn(isEdit ? "mt-5" : "mt-4", "mb-4")}
                                    onClick={async () => onGoBack()}
                                />
                            </>
                        )}

                        {isEdit && (
                            <div className="bancontact-edit-container">
                                <p className="edit-mode-text mt-5 mb-4 text-center">{t("bancontactOnboarding.enabledNote")}</p>
                                <div className="bancontact-edit-form my-5">
                                    <LegalTextLinkPopup
                                        legalTextBeforeLink=""
                                        legalTextAfterLink=""
                                        linkText={t("plugins.onboardedDescription.aeraTerms") + t("serviceNames.bancontact")}
                                        termsType={"TermsBancontact"}
                                        className="mt-5 checkbox-text"
                                        centered
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </PageContainer>
                {isEdit && (
                    <SharedCancelContainer
                        cancelText={t("bancontactOnboarding.cancel.buttonText", { webshopName: webshopName })}
                        onCancel={() => setShowCancelService(true)}
                    />
                )}
            </PageWrapper>
        </>
    )
}

export default BancontactPage
